import { Badge } from '#app/components/ui/badge.tsx'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import {
	EQUIPMENT_CATEGORIES,
	EQUIPMENT_TYPES,
} from '../equipment-cost-estimator.data.ts'

const CATEGORY_BORDER_COLOR: Record<string, string> = {
	'lawn-garden': 'border-t-green-500',
	'concrete-masonry': 'border-t-slate-500',
	'demolition-excavation': 'border-t-orange-500',
	'lifts-access': 'border-t-sky-500',
	'floor-care': 'border-t-violet-500',
	'power-tools': 'border-t-amber-500',
	'generators-power': 'border-t-yellow-500',
	'material-handling': 'border-t-indigo-500',
}

const CATEGORY_ICON_BG: Record<string, string> = {
	'lawn-garden': 'bg-green-100 dark:bg-green-900/50',
	'concrete-masonry': 'bg-slate-100 dark:bg-slate-800/50',
	'demolition-excavation': 'bg-orange-100 dark:bg-orange-900/50',
	'lifts-access': 'bg-sky-100 dark:bg-sky-900/50',
	'floor-care': 'bg-violet-100 dark:bg-violet-900/50',
	'power-tools': 'bg-amber-100 dark:bg-amber-900/50',
	'generators-power': 'bg-yellow-100 dark:bg-yellow-900/50',
	'material-handling': 'bg-indigo-100 dark:bg-indigo-900/50',
}

const CATEGORY_ICON_COLOR: Record<string, string> = {
	'lawn-garden': 'text-green-600 dark:text-green-400',
	'concrete-masonry': 'text-slate-600 dark:text-slate-400',
	'demolition-excavation': 'text-orange-600 dark:text-orange-400',
	'lifts-access': 'text-sky-600 dark:text-sky-400',
	'floor-care': 'text-violet-600 dark:text-violet-400',
	'power-tools': 'text-amber-600 dark:text-amber-400',
	'generators-power': 'text-yellow-600 dark:text-yellow-400',
	'material-handling': 'text-indigo-600 dark:text-indigo-400',
}

export function EquipmentCategoryGrid({
	activeCategory,
	onCategoryChange,
}: {
	activeCategory: string
	onCategoryChange: (slug: string) => void
}) {
	function handleClick(slug: string) {
		onCategoryChange(slug)
		const el = document.getElementById(`category-${slug}`)
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' })
		}
	}

	return (
		<section>
			<div className="text-center">
				<Badge variant="secondary" className="mb-4">
					Free Price Lookup Tool
				</Badge>
				<h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
					DFW Equipment & Tool Rental Cost Estimator
				</h1>
				<p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
					Compare rental prices for 40+ equipment types from Dallas-Fort Worth
					vendors. Select a category below to view current rates.
				</p>
			</div>

			<div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
				{EQUIPMENT_CATEGORIES.map((cat) => {
					const isActive = activeCategory === cat.slug
					const typeCount = EQUIPMENT_TYPES.filter(
						(t) => t.categorySlug === cat.slug,
					).length

					return (
						<button
							key={cat.slug}
							onClick={() => handleClick(cat.slug)}
							className="text-left"
						>
							<Card
								className={`h-full border-t-4 transition-all hover:shadow-md ${CATEGORY_BORDER_COLOR[cat.slug] ?? ''} ${
									isActive
										? 'ring-primary/50 shadow-md ring-2'
										: ''
								}`}
							>
								<CardContent className="flex flex-col items-center gap-2 p-4 text-center sm:p-6">
									<div
										className={`flex size-10 items-center justify-center rounded-full ${
											isActive
												? (CATEGORY_ICON_BG[cat.slug] ?? 'bg-primary/20')
												: 'bg-muted'
										}`}
									>
										<Icon
											name={cat.icon as IconName}
											size="md"
											className={
												isActive
													? (CATEGORY_ICON_COLOR[cat.slug] ?? 'text-primary')
													: 'text-muted-foreground'
											}
										/>
									</div>
									<div>
										<h2 className="text-sm font-semibold sm:text-base">
											{cat.name}
										</h2>
										<p className="text-muted-foreground mt-0.5 text-xs">
											{typeCount} types
										</p>
									</div>
									<p className="text-muted-foreground hidden text-xs sm:block">
										{cat.description}
									</p>
								</CardContent>
							</Card>
						</button>
					)
				})}
			</div>

			{/* Quick-stats banner */}
			<div className="mt-8 grid grid-cols-3 gap-4">
				<div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-center dark:border-green-800 dark:bg-green-950/30">
					<p className="text-lg font-bold text-green-700 dark:text-green-300">
						{EQUIPMENT_TYPES.length}+
					</p>
					<p className="text-muted-foreground text-xs">Equipment Types</p>
				</div>
				<div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-center dark:border-blue-800 dark:bg-blue-950/30">
					<p className="text-lg font-bold text-blue-700 dark:text-blue-300">
						{EQUIPMENT_CATEGORIES.length}
					</p>
					<p className="text-muted-foreground text-xs">Categories</p>
				</div>
				<div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center dark:border-amber-800 dark:bg-amber-950/30">
					<p className="text-lg font-bold text-amber-700 dark:text-amber-300">
						4
					</p>
					<p className="text-muted-foreground text-xs">Rate Options</p>
				</div>
			</div>
		</section>
	)
}
